import type { Session } from '@didaskill/domain/sessions/types';
import { STREAK_DAY_TYPES } from '@didaskill/domain/users/constants';
import dayjs from '@didaskill/services/dayjs';

/**
 * Check if the last session was on Friday, Saturday, or Sunday
 * of the previous week.
 *
 * @param lastActiveTimestamp - The timestamp of the last session
 * @returns true if the last session was on the previous weekend
 */
export function wasLastSessionOnPreviousWeekend(lastActiveTimestamp: string): boolean {
  // Number of days since the last session
  const daysSinceLastSession = dayjs()
    .startOf('day')
    .diff(dayjs(lastActiveTimestamp).startOf('day'), 'day');

  // Day index of the last session (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
  const lastSessionDay = dayjs(lastActiveTimestamp).day();

  // Check if the last session was on Friday, Saturday, or Sunday of the previous week
  return [5, 6, 0].includes(lastSessionDay) && daysSinceLastSession <= 3;
}

/**
 * Compute the connection day streak types for a given week.
 *
 * @param {dayjs.Dayjs} currentDay - The current day from which streaks are calculated.
 * @param {string[]} connectionDays - An array of connection days in ISO string format.
 * @returns {Record<string, STREAK_DAY_TYPES>} An object where keys are dates and values are their corresponding streak types.
 */
export const getStreaksFromConnectionDays = (
  currentDay: dayjs.Dayjs,
  connectionDays: string[],
): Record<string, STREAK_DAY_TYPES> => {
  const streakTypes: Record<string, STREAK_DAY_TYPES> = {};

  const startOfWeek = dayjs(currentDay).startOf('week').weekday(0);
  const endOfWeek = currentDay.endOf('week');
  const today = dayjs();

  for (let date = endOfWeek; date >= startOfWeek; date = date.subtract(1, 'day')) {
    const stringifiedDate = date.toISOString();
    const wasUserConnectedOnCurrentDay = connectionDays.find((cd) => dayjs(cd).isSame(date, 'day'));

    const isToday = date.isSame(today, 'day');
    const isWeekEnd = date.day() === 6 || date.day() === 0;

    if (wasUserConnectedOnCurrentDay && !isWeekEnd) {
      streakTypes[stringifiedDate] = isToday
        ? STREAK_DAY_TYPES.CURRENT_DAY
        : STREAK_DAY_TYPES.CONNECTED;
    } else {
      streakTypes[stringifiedDate] = isWeekEnd
        ? STREAK_DAY_TYPES.WEEK_END
        : STREAK_DAY_TYPES.NOT_CONNECTED;
    }
  }

  return streakTypes;
};

/**
 * Compute user daily connection streak.
 * @param {number} lastConnectionStreak - Daily connection streak value on last session.
 * @param {string} lastActiveTimestamp - Timestamp from last user session.
 * @param {number} daysSinceLastSession - Number of days since last user session.
 * @returns {number} new user connection streak.
 */
export const computeNewDailyConnectionStreak = ({
  lastConnectionStreak,
  lastActiveTimestamp,
  daysSinceLastSession,
}: {
  lastConnectionStreak: number;
  lastActiveTimestamp: string;
  daysSinceLastSession: number;
}): number => {
  let newDailyConnectionStreak = 1;

  const todayDay = dayjs().day();

  const isMonday = todayDay === 1;
  const isWeekEnd = todayDay === 6 || todayDay === 0;

  // Check if the last session was on Friday, Saturday, or Sunday of the previous week
  const isLastSessionOnPreviousWeekend = wasLastSessionOnPreviousWeekend(lastActiveTimestamp);

  /**
   * Cases we keep last streak :
   * - last session was today
   * - today is week-end
   */
  if (daysSinceLastSession === 0 || isWeekEnd) {
    newDailyConnectionStreak = lastConnectionStreak;
    /**
     * Cases we increment streak :
     * - last session was yesterday and today is not a weekend
     * - today is Monday and the last session was on Friday, Saturday, or Sunday of the previous week
     */
  } else if (
    (daysSinceLastSession === 1 && !isWeekEnd) ||
    (isMonday && isLastSessionOnPreviousWeekend)
  ) {
    newDailyConnectionStreak = lastConnectionStreak + 1;
  }

  return newDailyConnectionStreak;
};

/**
 * Compute the next day session streak from the last session.
 * @param lastSession - Last session.
 * @returns - Next day session streak.
 */
export function computeNextDaySessionStreakFromLastSession(
  lastSession: Session | null | undefined,
): number {
  if (!lastSession) {
    return 1;
  }

  const daysSinceLastSession = dayjs()
    .startOf('day')
    .diff(dayjs(lastSession.firstActiveTimestamp).startOf('day'), 'day');

  let param = 0;

  if ((lastSession.daySessionStreak ?? 0) > 0 && daysSinceLastSession === 0) {
    param = lastSession.daySessionStreak!;
  }

  return param ? param + 1 : 1;
}
